import { createContext, useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";
import useOrganizations, { OrganizationFetchConfig } from "@/features/organizations/useOrganizations";
import { Organization, Website } from "@/types";
import { useAppSelector } from "@/hooks";
import MetaTags from "@/components/MetaTags";

export type OrganizationProviderType = Partial<Organization> & {
  activeWebsite?: Website;
};
export const OrganizationProvider = createContext<OrganizationProviderType>({});

const UserOrganizationProvider = (props: OrganizationFetchConfig) => {
  const { currentOrganization, getOrgDetails } = useOrganizations();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);

  const activeWebsite = useMemo(() => {
    return currentOrganization?.websites?.find((website) => currentOrganization?.website === website.id);
  }, [currentOrganization?.website, currentOrganization?.websites]);

  useEffect(() => {
    if (organizationId) {
      getOrgDetails(props);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const googleVerificationMetaTag = useMemo(() => {
    if (!currentOrganization?.googleDomainVerificationToken) return null;
    
    const regex = /name="([^"]+)"\s+content="([^"]+)"/;
    const match = currentOrganization.googleDomainVerificationToken.match(regex);

    if (!match) return null;

    return {
      name: match[1],
      content: match[2],
    }
  }, [currentOrganization?.googleDomainVerificationToken])

  return (
    <OrganizationProvider.Provider
    value={{
      ...currentOrganization,
      activeWebsite,
    }}
    >
      <MetaTags
        name={currentOrganization?.title}
        title={activeWebsite?.metaData?.title}
        description={activeWebsite?.metaData?.description}
        image={currentOrganization?.isotype ?? currentOrganization?.logo}
        googleVerificationMetaTag={googleVerificationMetaTag}
      />
      <Outlet />
    </OrganizationProvider.Provider>
  );
};

export default UserOrganizationProvider;
